import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, Save, CheckCircle, Circle } from 'lucide-react';

/**
 * Wedding Details Checklist
 * Comprehensive form for capturing all wedding-specific requirements.
 * Data is stored as JSONB in event.weddingDetails.
 * Sections mirror the Errigal Country House Hotel Function Details Checklist.
 */

const SECTIONS = [
  {
    key: 'ceremony',
    title: 'Ceremony Details',
    fields: [
      { key: 'ceremonyType', label: 'Ceremony Type', type: 'select', options: ['Church', 'Civil Ceremony (on-site)', 'Humanist', 'Spiritual', 'Other'] },
      { key: 'ceremonyVenue', label: 'Ceremony Venue / Location', type: 'text' },
      { key: 'ceremonyTime', label: 'Ceremony Time', type: 'time' },
      { key: 'civilCeremonyRoom', label: 'Civil Ceremony Room (if on-site)', type: 'text' },
      { key: 'officiantName', label: 'Officiant / Celebrant Name', type: 'text' },
    ],
  },
  {
    key: 'arrival',
    title: 'Arrival & Reception',
    fields: [
      { key: 'arrivalTime', label: 'Guest Arrival Time at Hotel', type: 'time' },
      { key: 'arrivalReceptionSuite', label: 'Arrival Reception Suite', type: 'text' },
      { key: 'arrivalDrinks', label: 'Arrival Drinks / Canapés', type: 'textarea' },
      { key: 'arrivalMusic', label: 'Arrival Music (live / DJ / playlist)', type: 'text' },
      { key: 'entranceSong', label: 'Entrance Song for Couple', type: 'text' },
      { key: 'carDetails', label: 'Wedding Car Details', type: 'text' },
    ],
  },
  {
    key: 'topTable',
    title: 'Top Table & Bridal Party',
    fields: [
      { key: 'topTableNames', label: 'Top Table Names (in order)', type: 'textarea', placeholder: 'List names in seating order, left to right' },
      { key: 'topTableCount', label: 'No. at Top Table', type: 'number' },
      { key: 'bestMan', label: 'Best Man Name', type: 'text' },
      { key: 'maidOfHonour', label: 'Maid of Honour / Chief Bridesmaid', type: 'text' },
      { key: 'bridesmaidsDressColour', label: 'Bridesmaids Dress Colour', type: 'text' },
      { key: 'groomsmenCount', label: 'No. of Groomsmen', type: 'number' },
      { key: 'bridesmaidCount', label: 'No. of Bridesmaids', type: 'number' },
      { key: 'flowergirl', label: 'Flower Girl(s)', type: 'text' },
      { key: 'pageboy', label: 'Page Boy(s)', type: 'text' },
    ],
  },
  {
    key: 'meal',
    title: 'Meal & Service',
    fields: [
      { key: 'mealCallTime', label: 'Meal Call Time', type: 'time' },
      { key: 'mealServedTime', label: 'Meal Served Time', type: 'time' },
      { key: 'graceBy', label: 'Grace Said By', type: 'text' },
      { key: 'speechesWhen', label: 'Speeches — Before or After Meal?', type: 'select', options: ['Before Meal', 'After Meal', 'During Meal'] },
      { key: 'speechOrder', label: 'Speech Order', type: 'textarea', placeholder: 'e.g. Father of Bride, Groom, Best Man' },
    ],
  },
  {
    key: 'menu',
    title: 'Menu Details',
    fields: [
      { key: 'menuPackage', label: 'Menu Package / Price per Head', type: 'text' },
      { key: 'starter', label: 'Starter', type: 'textarea' },
      { key: 'soup', label: 'Soup', type: 'text' },
      { key: 'sorbet', label: 'Sorbet / Palate Cleanser', type: 'text' },
      { key: 'mainCourse', label: 'Main Course', type: 'textarea' },
      { key: 'dessert', label: 'Dessert', type: 'textarea' },
      { key: 'eveningFood', label: 'Evening Food', type: 'textarea' },
      { key: 'childrenMenu', label: 'Children\'s Menu', type: 'textarea' },
      { key: 'dietaryNotes', label: 'Dietary Requirements / Allergies', type: 'textarea' },
    ],
  },
  {
    key: 'drinks',
    title: 'Wine & Drinks',
    fields: [
      { key: 'wineSelection', label: 'Wine Selection (with meal)', type: 'textarea' },
      { key: 'toastDrink', label: 'Toast Drink', type: 'text' },
      { key: 'cashBar', label: 'Cash Bar / Open Bar Details', type: 'text' },
      { key: 'corkage', label: 'Corkage / Brought-in Drinks', type: 'text' },
    ],
  },
  {
    key: 'decor',
    title: 'Décor & Extras',
    fields: [
      { key: 'flowers', label: 'Flowers / Ivy Garland', type: 'textarea' },
      { key: 'napkins', label: 'Napkin Colour / Style', type: 'text' },
      { key: 'placeCards', label: 'Place Cards', type: 'text' },
      { key: 'tablePlan', label: 'Table Plan Details', type: 'text' },
      { key: 'favours', label: 'Favours / Gifts on Tables', type: 'text' },
      { key: 'sweetCart', label: 'Sweet Cart', type: 'text' },
      { key: 'postBox', label: 'Post Box / Card Box', type: 'text' },
      { key: 'candlelight', label: 'Candles / Candlelight', type: 'text' },
    ],
  },
  {
    key: 'cake',
    title: 'Cake',
    fields: [
      { key: 'cakeSupplier', label: 'Cake Supplier', type: 'text' },
      { key: 'cakeType', label: 'Cake Type / Flavour', type: 'text' },
      { key: 'cakeTiers', label: 'No. of Tiers', type: 'number' },
      { key: 'cakeCuttingTime', label: 'Cake Cutting Time', type: 'time' },
      { key: 'cakeServed', label: 'Serve Cake As...', type: 'select', options: ['Dessert', 'Evening food', 'On a table for guests', 'Other'] },
    ],
  },
  {
    key: 'entertainment',
    title: 'Entertainment & Music',
    fields: [
      { key: 'band', label: 'Band / DJ Name', type: 'text' },
      { key: 'bandStartTime', label: 'Band / DJ Start Time', type: 'time' },
      { key: 'firstDance', label: 'First Dance Song', type: 'text' },
      { key: 'firstDanceTime', label: 'First Dance Time', type: 'time' },
      { key: 'otherEntertainment', label: 'Other Entertainment (photo booth, magician, etc.)', type: 'textarea' },
    ],
  },
  {
    key: 'suppliers',
    title: 'Suppliers & Contacts',
    fields: [
      { key: 'photographer', label: 'Photographer', type: 'text' },
      { key: 'videographer', label: 'Videographer', type: 'text' },
      { key: 'florist', label: 'Florist', type: 'text' },
      { key: 'hairMakeup', label: 'Hair & Makeup', type: 'text' },
      { key: 'weddingPlanner', label: 'Wedding Planner / Coordinator', type: 'text' },
      { key: 'otherSuppliers', label: 'Other Suppliers', type: 'textarea' },
    ],
  },
  {
    key: 'accommodation',
    title: 'Accommodation & Logistics',
    fields: [
      { key: 'bridalSuite', label: 'Bridal Suite', type: 'text' },
      { key: 'roomsBlocked', label: 'No. of Rooms Blocked / Reserved', type: 'number' },
      { key: 'roomRate', label: 'Room Rate for Guests', type: 'text' },
      { key: 'nextDayBrunch', label: 'Next Day Brunch / BBQ', type: 'text' },
      { key: 'busTransport', label: 'Bus / Transport for Guests', type: 'text' },
      { key: 'specialAccess', label: 'Wheelchair / Special Access Needs', type: 'text' },
    ],
  },
  {
    key: 'billing',
    title: 'Billing & Payment Notes',
    fields: [
      { key: 'totalQuote', label: 'Total Quoted (€)', type: 'number' },
      { key: 'depositAmount', label: 'Deposit Amount (€)', type: 'number' },
      { key: 'paymentScheduleNotes', label: 'Payment Schedule Notes', type: 'textarea' },
      { key: 'billingNotes', label: 'Additional Billing Notes', type: 'textarea' },
    ],
  },
];

function SectionAccordion({ section, data, onChange, isOpen, onToggle }) {
  const filledCount = section.fields.filter(
    (f) => data[f.key] && String(data[f.key]).trim() !== ''
  ).length;
  const totalCount = section.fields.length;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition"
      >
        <div className="flex items-center gap-3">
          {filledCount === totalCount ? (
            <CheckCircle size={18} className="text-emerald-600" />
          ) : (
            <Circle size={18} className="text-gray-400" />
          )}
          <span className="font-medium text-sm text-gray-900">{section.title}</span>
          <span className="text-xs text-gray-500">
            {filledCount}/{totalCount}
          </span>
        </div>
        {isOpen ? <ChevronUp size={16} className="text-gray-500" /> : <ChevronDown size={16} className="text-gray-500" />}
      </button>
      {isOpen && (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {section.fields.map((field) => (
            <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  value={data[field.key] || ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  placeholder={field.placeholder || ''}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              ) : field.type === 'select' ? (
                <select
                  value={data[field.key] || ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select...</option>
                  {field.options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={field.type}
                  value={data[field.key] || ''}
                  onChange={(e) => onChange(field.key, e.target.value)}
                  placeholder={field.placeholder || ''}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WeddingChecklist({ event, onSave }) {
  const [formData, setFormData] = useState({});
  const [openSections, setOpenSections] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  // Initialise from event.weddingDetails
  useEffect(() => {
    if (event?.weddingDetails && typeof event.weddingDetails === 'object') {
      setFormData(event.weddingDetails);
    } else {
      setFormData({});
    }
    setIsDirty(false);
  }, [event?.id]);

  const handleFieldChange = useCallback((key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
    setSaveMessage('');
  }, []);

  const toggleSection = useCallback((sectionKey) => {
    setOpenSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  }, []);

  const handleSave = useCallback(() => {
    if (onSave) {
      onSave(formData);
      setIsDirty(false);
      setSaveMessage('Saved!');
      setTimeout(() => setSaveMessage(''), 2000);
    }
  }, [formData, onSave]);

  // Overall progress
  const totalFields = SECTIONS.reduce((sum, s) => sum + s.fields.length, 0);
  const filledFields = SECTIONS.reduce(
    (sum, s) =>
      sum + s.fields.filter((f) => formData[f.key] && String(formData[f.key]).trim() !== '').length,
    0
  );
  const progressPct = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${progressPct}%`, backgroundColor: '#1A5C3A' }}
          />
        </div>
        <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
          {filledFields}/{totalFields} ({progressPct}%)
        </span>
      </div>

      {/* Accordion sections */}
      <div className="space-y-2">
        {SECTIONS.map((section) => (
          <SectionAccordion
            key={section.key}
            section={section}
            data={formData}
            onChange={handleFieldChange}
            isOpen={!!openSections[section.key]}
            onToggle={() => toggleSection(section.key)}
          />
        ))}
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={handleSave}
          disabled={!isDirty}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white transition ${
            isDirty ? 'hover:opacity-90' : 'opacity-50 cursor-not-allowed'
          }`}
          style={{ backgroundColor: '#1A5C3A' }}
        >
          <Save size={16} />
          Save Wedding Details
        </button>
        {saveMessage && (
          <span className="text-sm text-emerald-600 font-medium flex items-center gap-1">
            <CheckCircle size={14} /> {saveMessage}
          </span>
        )}
      </div>
    </div>
  );
}
